import React from "react";
import {
  ClockIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";

interface SalespersonUsageDisplayProps {
  secondsUsed: number;
  secondsLimit: number | null;
  periodEnd: string;
  compact?: boolean;
}

const SalespersonUsageDisplay: React.FC<SalespersonUsageDisplayProps> = ({
  secondsUsed,
  secondsLimit,
  periodEnd,
  compact = false,
}) => {
  // Move formatDate here, before it's used
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // If no limit, show unlimited
  if (secondsLimit === null) {
    const minutesUsed = Math.floor(secondsUsed / 60);

    if (compact) {
      return (
        <div className="inline-flex items-center px-3 py-1.5 rounded-md border border-gray-200 bg-gray-50">
          <ClockIcon className="h-4 w-4 mr-2 text-gray-400" />
          <span className="text-sm font-medium text-gray-700">
            {minutesUsed.toLocaleString()} min uploaded (Unlimited)
          </span>
        </div>
      );
    }

    return (
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-center space-x-2 mb-2">
          <ClockIcon className="h-5 w-5 text-gray-400" />
          <h3 className="text-sm font-medium text-gray-900">
            Your Monthly Usage
          </h3>
        </div>
        <div className="space-y-2">
          <div className="flex justify-between items-baseline">
            <span className="text-2xl font-bold text-gray-900">
              {minutesUsed.toLocaleString()}
            </span>
            <span className="text-sm text-gray-500">minutes uploaded</span>
          </div>
          <p className="text-sm text-gray-500">
            No monthly limit set • Resets {formatDate(periodEnd)}
          </p>
        </div>
      </div>
    );
  }

  // Has a limit - show full usage display
  const minutesUsed = Math.floor(secondsUsed / 60);
  const minutesLimit = Math.floor(secondsLimit / 60);
  const percentage = (secondsUsed / secondsLimit) * 100;

  const getStatusColor = () => {
    if (percentage >= 100) return "text-red-600 bg-red-50 border-red-200";
    if (percentage >= 90)
      return "text-orange-600 bg-orange-50 border-orange-200";
    if (percentage >= 80)
      return "text-yellow-600 bg-yellow-50 border-yellow-200";
    return "text-green-600 bg-green-50 border-green-200";
  };

  const getProgressBarColor = () => {
    if (percentage >= 100) return "bg-red-600";
    if (percentage >= 90) return "bg-orange-500";
    if (percentage >= 80) return "bg-yellow-500";
    return "bg-green-500";
  };

  if (compact) {
    return (
      <div
        className={`inline-flex items-center px-3 py-1.5 rounded-md border ${getStatusColor()}`}
      >
        <ClockIcon className="h-4 w-4 mr-2" />
        <span className="text-sm font-medium">
          {minutesUsed.toLocaleString()} / {minutesLimit.toLocaleString()} min
        </span>
        {percentage >= 90 && (
          <ExclamationTriangleIcon className="h-4 w-4 ml-2" />
        )}
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center space-x-2">
          <ClockIcon className="h-5 w-5 text-gray-400" />
          <h3 className="text-sm font-medium text-gray-900">
            Your Monthly Usage
          </h3>
        </div>
        {percentage >= 90 && (
          <ExclamationTriangleIcon className="h-5 w-5 text-orange-500" />
        )}
      </div>

      <div className="space-y-2">
        <div className="flex justify-between items-baseline">
          <span className="text-2xl font-bold text-gray-900">
            {minutesUsed.toLocaleString()}
          </span>
          <span className="text-sm text-gray-500">
            of {minutesLimit.toLocaleString()} minutes
          </span>
        </div>

        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all duration-300 ${getProgressBarColor()}`}
            style={{ width: `${Math.min(percentage, 100)}%` }}
          />
        </div>

        <div className="flex justify-between text-xs text-gray-500">
          <span>{percentage.toFixed(1)}% used</span>
          <span>Resets {formatDate(periodEnd)}</span>
        </div>

        {percentage >= 100 && (
          <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-800">
            <strong>Limit Reached:</strong> You've reached your monthly limit.
            Contact your manager to upload more.
          </div>
        )}
        {percentage >= 90 && percentage < 100 && (
          <div className="mt-2 p-2 bg-orange-50 border border-orange-200 rounded text-xs text-orange-800">
            <strong>Warning:</strong> You're approaching your monthly limit.
          </div>
        )}
      </div>
    </div>
  );
};

export default SalespersonUsageDisplay;
